import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/CustomerQuotations.jsx. Status
// labels come from the `quotation_status` List (System > Lists) via
// labelFor() rather than the source's hardcoded STATUS_LABELS map — the
// badge *color* below still switches on the raw status string since that's
// fixed business logic (see CustomerQuotationController), only the display
// text is admin-configurable.
function statusBadgeClass(status: string): string {
  if (['accepted', 'converted_to_invoice'].includes(status)) return 'badge-normal';
  if (['rejected', 'expired'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

interface QuotationRow {
  id: number;
  quotation_number: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  service: string;
  amount: number;
  status: string;
}

export function CustomerQuotations() {
  const { settings, lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(
    () => ({ search: search || undefined, status: status || undefined }),
    [search, status]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<QuotationRow>(
    '/customer-quotations',
    filters
  );

  const remove = async (q: QuotationRow) => {
    if (!(await confirmDelete(`Delete quotation ${q.quotation_number}?`))) return;
    try {
      await api.delete(`/customer-quotations/${q.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<QuotationRow>(
      api,
      '/customer-quotations',
      { search: search || undefined, status: status || undefined },
      [
        { label: 'Number', value: (r) => r.quotation_number },
        { label: 'Customer', value: (r) => r.customer_name },
        { label: 'Phone', value: (r) => r.customer_phone ?? '' },
        { label: 'Email', value: (r) => r.customer_email ?? '' },
        { label: 'Service', value: (r) => r.service },
        { label: 'Amount', value: (r) => r.amount },
        { label: 'Status', value: (r) => labelFor(lists, 'quotation_status', r.status) },
      ],
      'quotations.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Quotations</h1>
        <Link className="btn btn-primary" to="/revenue/quotations/new">
          + Add quotation
        </Link>
      </header>
      <p className="muted">Quotations sent to customers for hall rentals, classes, events, and other services.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search number, customer or service…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {listOptions(lists, 'quotation_status').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Customer</th>
                <th>Service</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((q) => (
                  <tr key={q.id}>
                    <td className="muted">{q.quotation_number}</td>
                    <td>{q.customer_name}</td>
                    <td>{q.service}</td>
                    <td className="right">{fmtMoney(q.amount)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(q.status)}`}>
                        {labelFor(lists, 'quotation_status', q.status)}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/revenue/quotations/${q.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/revenue/quotations/${q.id}?edit=1`} />
                      <IconButton
                        icon={IconTrash}
                        label="Delete"
                        variant="danger-ghost"
                        onClick={() => remove(q)}
                        hidden={q.status !== 'draft'}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No quotations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
